import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../../environments/environment';

/**
 * Mirrors ORF.KIUT.Core.Services.FleetInstanceClass. "Foreign" never reaches
 * the client — the backend drops those before answering.
 */
export type FleetInstanceClass =
  | 'Permanent'
  | 'AutoScaled'
  | 'VoxtralRealtime'
  | 'WhisperLive';

export interface FleetInstance {
  instanceId: string;
  name: string | null;
  role: string | null;
  channel: string | null;
  broadcastKey: string | null;
  instanceType: string;
  state: string;
  class: FleetInstanceClass;
  autoScalingGroupName: string | null;
}

export interface FleetAutoScalingGroup {
  name: string;
  minSize: number;
  maxSize: number;
  desiredCapacity: number;
  suspendedProcesses: string[];
  instanceCount: number;
}

export interface FleetActionRecord {
  action: string;
  actor: string;
  atUtc: string;
}

export interface LambdaFunctionInfo {
  name: string;
  /** Reserved concurrency is 0 — this feature throttled it. */
  throttled: boolean;
  reservedConcurrency: number | null;
}

export interface EventTriggerInfo {
  /** "rule", "event-source-mapping" or "schedule". */
  kind: string;
  name: string;
  enabled: boolean;
}

export interface EcsTaskInfo {
  cluster: string;
  taskArn: string;
  family: string;
  lastStatus: string;
}

export interface ServerlessFleetStatus {
  functions: LambdaFunctionInfo[];
  triggers: EventTriggerInfo[];
  tasks: EcsTaskInfo[];
}

export interface FleetCategoryProgress {
  name: string;
  total: number;
  done: number;
  /** Capped at 5 - the panel wants "which ones", not all of them. */
  pending: string[];
  converged: boolean;
}

export interface FleetProgress {
  target: string;
  converged: boolean;
  categories: FleetCategoryProgress[];
}

export interface FleetStatus {
  stage: string;
  timestampUtc: string;
  instances: FleetInstance[];
  autoScalingGroups: FleetAutoScalingGroup[];
  lastAction: FleetActionRecord | null;
  /** Null when the serverless read failed; the EC2 view is still valid. */
  serverless: ServerlessFleetStatus | null;
  /** Recurring backend jobs are standing down (up to ~15s cache lag per pod). */
  jobsPaused: boolean;
  /** Convergence toward the recorded target; the reconciler drives it. */
  progress: FleetProgress | null;
}

export interface FleetActionResult {
  action: string;
  stage: string;
  started: string[];
  stopped: string[];
  terminated: string[];
  autoScalingGroupsChanged: string[];
  notes: string[];
}

/**
 * 2026-09-07 — power control for the environment this portal is deployed into.
 *
 * There is deliberately no environment parameter: dev, stg and prd live in
 * separate AWS accounts and each portal holds a role only in its own, so the
 * backend takes the stage from its own configuration. Hibernating stg means
 * opening the stg portal.
 */
@Injectable({ providedIn: 'root' })
export class EnvironmentFleetService {

  private readonly baseUrl = `${environment.API_Base}/admin/environment`;

  constructor(private http: HttpClient) {}

  getStatus(): Observable<FleetStatus> {
    return this.http.get<FleetStatus>(this.baseUrl);
  }

  activate(): Observable<FleetActionResult> {
    return this.http.post<FleetActionResult>(`${this.baseUrl}/activate`, {});
  }

  hibernate(force = false): Observable<FleetActionResult> {
    return this.http.post<FleetActionResult>(`${this.baseUrl}/hibernate`, { force });
  }

  /** `confirm` must equal the stage name, e.g. "prd". */
  deactivate(confirm: string, force = false): Observable<FleetActionResult> {
    return this.http.post<FleetActionResult>(`${this.baseUrl}/deactivate`, { confirm, force });
  }
}
